import { CloseButton, SettingsButton, SaveButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import {
  DateRangeTypeOption,
  IDateRange,
} from '../../../../data/providers/settings/dates/dateRangeProvider';
import {
  asymmetryReportSettingsProvider,
  IAsymmetryReportSettings,
  IAsymmetryReportSettingsState,
} from '../../../../data/providers/settings/reports/asymmetryReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import { isValidDateRange } from '../../../../data/providers/utils/isValidDateRange';
import { IRange } from '../../../../data/types';
import {
  deleteSelectedTemplate,
  saveSettings,
} from '../../../../data/writers/settings/reports/asymmetryReportSettings';
import { KEYS } from '../../../../keys';
import { Row } from '../../../layout/Row';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { DateSelect } from '../../../shared/form/selects/DateRangeSelect';
import { Step } from '../../../shared/form/Step';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { FormSection } from '../../utils/settings/FormSection';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateSelect } from '../../utils/settings/TemplateSelect';
import { TimeframeSelector } from '../../utils/settings/TimeframeSelector';
import { TemplateForm } from './TemplateForm';

interface IPopupProps {
  onClose: () => void;
  onSave: () => void;
}

export const Popup = (props: IPopupProps): React.ReactElement => {
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ISettingsTemplate<IAsymmetryReportSettings> | null>();
  const [templates, setTemplates] = React.useState<
    Array<ISettingsTemplate<IAsymmetryReportSettings>>
  >([]);
  const [initialSettings, setInitialSettings] =
    React.useState<ISettingsTemplate<IAsymmetryReportSettings> | null>();
  // This report will always be a two-date comparison
  const [timeframe, setTimeframe] = React.useState<IDateRange>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [baselineTimeframe, setBaselineTimeframe] =
    React.useState<IDateRange>();

  /**
   * @description subscribe to block report settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = asymmetryReportSettingsProvider().subscribe(
      (mSettings: IAsymmetryReportSettingsState): void => {
        setTemplates(mSettings?.templates);
        setSelectedTemplate(mSettings?.selected);
        setInitialSettings(mSettings?.selected);

        /// dont override timeframe unless it actually has a value
        if (!isEmpty(mSettings?.timeframe)) {
          setTimeframe(mSettings?.timeframe);
        }
        if (!isEmpty(mSettings?.baselineTimeframe)) {
          setBaselineTimeframe(mSettings?.baselineTimeframe);
        }
        setLoading(false);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description saves the report settings to database
   */
  const handleSave = async (): Promise<void> => {
    /// make sure there was an actual change
    if (!isEqual(selectedTemplate?.id, initialSettings?.id)) {
      props.onSave();
    }
    setLoading(true);
    await saveSettings(selectedTemplate!, timeframe, baselineTimeframe);
    props.onClose();
  };

  /**
   * @description handles change to the selected template
   * @param mSelectedTemplate
   */
  const templateChangeHandler = (
    mSelectedTemplate: ISettingsTemplate<IAsymmetryReportSettings>
  ): void => {
    setSelectedTemplate(mSelectedTemplate);
  };

  /**
   * @description deletes the supplied template and finds a suitable replacement for the selected template
   * @param template
   */
  const templateDeleteHandler = async (
    template: ISettingsTemplate<IAsymmetryReportSettings>
  ): Promise<void> => {
    const index = templates.findIndex(
      (tmp: ISettingsTemplate<IAsymmetryReportSettings>): boolean =>
        tmp.id === template.id
    );
    /// get either the template before or after the template being deleted
    const replacement: ISettingsTemplate<IAsymmetryReportSettings> =
      index === 0 ? templates?.[1] : templates?.[index - 1];

    await deleteSelectedTemplate(template, replacement);
  };

  /**
   * @description handles the change to the timeframe
   * @param range
   */
  const timeframeHandler = (range: IDateRange): void => {
    setTimeframe(range);
  };

  /**
   * @description handle changes to baseline timeframe
   * @param dateType
   * @param dateRange
   */
  const baselineTimeframeChangeHandler = (
    dateType: DateRangeTypeOption,
    dateRange: IRange
  ): void => {
    setBaselineTimeframe({
      dateType,
      dateRange: dateRange ?? null,
    });
  };

  const isValid = React.useMemo((): boolean => {
    /// make sure the user has selected a template
    if (isEmpty(selectedTemplate)) return false;
    /// make sure the user has selected a valid timeframe
    if (!isValidDateRange(timeframe)) return false;
    /// make sure the user has selected a baseline timeframe
    if (!isValidDateRange(baselineTimeframe)) return false;
    /// form is valid and can be submitted
    return true;
  }, [
    JSON.stringify(timeframe),
    JSON.stringify(selectedTemplate),
    JSON.stringify(baselineTimeframe),
  ]);

  return (
    <PopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Asymmetry Report Settings</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        {loading && <LinearLoading />}
        <CardBody>
          <Row shouldCollapse style={{ height: '100%' }}>
            <LeftColumn>
              <TemplateSelect
                step={1}
                templates={templates}
                onSelect={templateChangeHandler}
                onDelete={templateDeleteHandler}
                selectedTemplateId={selectedTemplate?.id}
                formComponent={TemplateForm}
                reportType={'Asymmetry Report'}
                reportTypeId={'asymmetryReport'}
                testId={KEYS.ASYMMETRY_REPORT_TEMPLATE_LIST}
                searchTestId={KEYS.ASYMMETRY_REPORT_TEMPLATE_SEARCH}
              />
            </LeftColumn>
            <RightColumn>
              <TimeframeSelector
                onTimeframeChange={timeframeHandler}
                timeframe={timeframe}
                step={2}
                id={KEYS.ASYMMETRY_REPORT_TIMEFRAME_SELECT}
              />
              <FormSection>
                <CardHeader>
                  <CardHeaderLeft>
                    <Step>3</Step>
                    <h6>Select Baseline Timeframe</h6>
                  </CardHeaderLeft>
                </CardHeader>
                <div style={{ padding: '10px' }}>
                  <ControlSpacer>
                    <DateSelect
                      type='exclusive'
                      onChange={baselineTimeframeChangeHandler}
                      dateRangeType={baselineTimeframe?.dateType}
                      dateRange={baselineTimeframe?.dateRange}
                    />
                  </ControlSpacer>
                </div>
              </FormSection>
              <div
                style={{
                  border: '1px solid var(--color-text)',
                  padding: '10px',
                  marginTop: '10px',
                }}
              >
                <p>
                  The asymmetry report is versatile, supporting both bilateral
                  and unilateral testing. In the single athlete view, it shows
                  daily values over the chosen time period. Conversely, the
                  group view presents average asymmetry values from the selected
                  date range (e.g., the last 5 sessions). This report can
                  simultaneously display multiple test types and metrics, and
                  includes custom color coding based on your preferred
                  thresholds.
                </p>
              </div>
              <p
                style={{
                  marginTop: '15px',
                  fontSize: '1.2em',
                  color: 'var(--color-accent)',
                }}
              >
                <a
                  rel='noreferrer'
                  target='_blank'
                  href='https://www.hawkindynamics.com/blog/asymmetry-report'
                >
                  https://www.hawkindynamics.com/blog/asymmetry-report
                </a>
              </p>
            </RightColumn>
          </Row>
        </CardBody>

        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton
              onClick={handleSave}
              disabled={loading || !isValid}
              data-testid={KEYS.ASYMMETRY_REPORT_SETTINGS_SAVE_BUTTON}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  onSave: () => void;
}
export const AsymmetrySettings = withRouter(
  (props: IProps): React.ReactElement => {
    const open = (): void =>
      openPopup(
        <Popup onClose={closePopup(props.history)} onSave={props.onSave} />,
        props.history,
        'asymmetry-report-settings'
      );

    return (
      <SettingsButton
        onClick={open}
        style={props.style}
        data-testid={KEYS.ASYMMETRY_REPORT_SETTINGS_BUTTON}
      />
    );
  }
);
